/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { getByIdAsync, getVariantsByDimensionValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProductDimension, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import getHtmlParserValue from '../Utilities/get-html-parser-value';

// tslint:disable-next-line: completed-docs
export class BuyboxGetDimensionPricingInput implements IActionInput {
    public channelId: number;
    public productId: number;
    constructor(productId: number, channelId: number) {
        this.channelId = channelId;
        this.productId = productId;
    }
    public getCacheKey = () => `DimensionPrices`;
    public getCacheObjectType = () => 'Product';
    public dataCacheType = (): CacheType => 'none';
}

export interface IBuyboxGetDimensionPricingActionData {
    discountAmount?: number;
    name: string;
    price: number;
    productId: number;
    dimensionLabel: string;
}

const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    if (inputData && inputData.requestContext && inputData.requestContext.query && inputData.requestContext.query.productId) {
        return new BuyboxGetDimensionPricingInput(
            +inputData.requestContext.query.productId,
            +inputData.requestContext.apiSettings.channelId
        );
    } else if (inputData && inputData.requestContext && inputData.requestContext.urlTokens && inputData.requestContext.urlTokens.itemId) {
        return new BuyboxGetDimensionPricingInput(
            +inputData.requestContext.urlTokens.itemId,
            +inputData.requestContext.apiSettings.channelId
        );
    } else if (inputData && inputData.config && inputData.config.productId) {
        return new BuyboxGetDimensionPricingInput(inputData.config.productId, inputData.requestContext.apiSettings.channelId);
    } else {
        throw new Error('Unable to create SelectedVariantInput, no productId found on module config or query');
    }
};

const getDimensionLabel = (dimension: ProductDimension): string | undefined => {
    const extensionProperties = dimension.DimensionValue?.ExtensionProperties;

    let dimensionLabel;
    extensionProperties?.forEach(property => {
        if (property.Key?.toLowerCase() === 'description') {
            dimensionLabel = property.Value?.StringValue && getHtmlParserValue(property.Value?.StringValue);
        }
    });

    return dimensionLabel;
};

export async function getDimensionPricingAction(
    input: BuyboxGetDimensionPricingInput,
    ctx: IActionContext
): Promise<IBuyboxGetDimensionPricingActionData[]> {
    const pricingData: IBuyboxGetDimensionPricingActionData[] = [];

    const productResponse = await getByIdAsync(
        { callerContext: ctx, queryResultSettings: { Paging: { Top: 1 } } },
        input.productId,
        input.channelId
    );
    const baseProduct = Array.isArray(productResponse) ? productResponse[0] : productResponse;

    await getVariantsByDimensionValuesAsync(
        { callerContext: ctx, queryResultSettings: { Paging: { Top: 500 } } },
        baseProduct.MasterProductId || input.productId,
        input.channelId,
        []
    )
        .then(async (variants: SimpleProduct[]) => {
            // TODO: refactor code to use Hashmap
            // ex: https://codepen.io/asadalikanwal/pen/ZEpgmzd?editors=1111
            if (variants) {
                const dimensionsSizes = {};
                const productIds = [];
                variants.forEach(variant => {
                    variant.Dimensions?.forEach(dimension => {
                        if (dimension.DimensionTypeValue === 3) {
                            const sizeLabel = dimension.DimensionValue?.Value?.toLowerCase().trim();
                            const priceLabel = variant.Price;
                            const dimensionLabel = getDimensionLabel(dimension);
                            if (sizeLabel) {
                                if (dimensionsSizes.hasOwnProperty(sizeLabel)) {
                                    if (priceLabel && dimensionsSizes[sizeLabel].price >= priceLabel) {
                                        dimensionsSizes[sizeLabel] = {
                                            price: priceLabel,
                                            productId: variant.RecordId,
                                            name: dimension.DimensionValue?.Value,
                                            dimensionLabel: dimensionLabel
                                        };
                                    }
                                } else {
                                    dimensionsSizes[sizeLabel] = {
                                        price: priceLabel,
                                        productId: variant.RecordId,
                                        name: dimension.DimensionValue?.Value,
                                        dimensionLabel: dimensionLabel
                                    };
                                }
                            }
                        }
                    });
                });

                if (dimensionsSizes) {
                    // push all dimension/variant ids to array for activePrice call
                    for (const key of Object.keys(dimensionsSizes)) {
                        if (dimensionsSizes.hasOwnProperty(key)) {
                            pricingData.push(dimensionsSizes[key]);
                            productIds.push(dimensionsSizes[key].productId);
                        }
                    }
                }
            }
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            throw new Error('[IBuyboxGetDimensionPricingActionData]Unable to retieve data');
        });
    return pricingData;
}

export default createObservableDataAction({
    action: <IAction<IBuyboxGetDimensionPricingActionData[]>>getDimensionPricingAction,
    input: createInput
});
